"use client";

import { useEffect, useState, type CSSProperties } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import LibraryIcon, { type LibraryIconName } from "@/components/ui/LibraryIcon";
import { useAuth } from "@/hooks/useAuth";
import { apiFetch } from "@/lib/api/client";
import SpiritStripBookmark from "@/components/spirit/SpiritStripBookmark";
import PageBackground from "@/components/ui/PageBackground";
import SkillTreeGoals from "@/components/ui/SkillTreeGoals";
import { DailyMissionsCompact } from "@/components/ui/DailyMissions";
import CurrentLibraryPlaque from "@/components/home/CurrentLibraryPlaque";
import ContinueReadingCard, { type ReadHistoryEntry } from "@/components/home/ContinueReadingCard";
import UserLevelBadge from "@/components/home/UserLevelBadge";
import PhraseText from "@/components/book/PhraseText";
import type { Spirit } from "@/lib/types/spirit";
import type { DailyState } from "@/lib/daily-missions-server";
import { HOME_PIECE_WIDTH, type HomeLayoutPieces, type HomePieceCustoms, type HomePieceId } from "@/lib/home-layout";
import { destinationHref } from "@/lib/nav-tabs";
import { useUiTexts } from "@/lib/ui-texts-client";
import { LIBRARIAN_LINE_LINKS, resolveLibrarianSpeech, type LibrarianLine, type LibrarianLineFacts, type LibrarianLineValues } from "@/lib/librarian-lines";
import { FALLBACK_GACHA_COST } from "@/hooks/useGacha";
import { affordableDraws } from "@/lib/magic-supply";
import TextLines from "@/components/ui/TextLines";
import { PRESENT_COUNT_EVENT } from "@/components/ui/SkillUnlockToast";
import LoginBonusWindow from "@/components/quests/LoginBonusWindow";
import { GACHA_ENTRY_PATH } from "@/lib/gacha-entry";

/** ホームの司書。管理UIの「ホームの司書」（/librarian）で絵と明るさを決める */
interface HomeLibrarian {
  imageUrl: string;
  /** 司書を背景（黒い膜の下）より明るく残す量 */
  brightnessLift: number;
  /** 司書の中心の横位置。中身の幅（最大 448px）に対する割合 */
  positionX: number;
  /** 司書の絵の下端が画面の下端から何 px 上か（ナビより低いと足元がナビの裏に沈む） */
  positionBottom: number;
  /** 司書の大きさ。1 で 288×384px の枠（足元と横の中心はそのままで大きさだけ変わる） */
  scale: number;
}

/** 司書の絵の枠（大きさ 1 のとき）。管理UIの local-ui/src/types/home-librarian.ts の LIBRARIAN_BOX と同じ */
const LIBRARIAN_BOX = { width: 288, height: 384 };

/** ホームを一度でも開いたか（アプリを開き直すまで覚える）。2回目からは吹き出しの話を抽選で選ぶ */
let homeOpenedBefore = false;
/** 前にホームを離れたとき吹き出しに出ていた話（戻ってきたとき、同じ話を続けて出さない） */
let lastHomeSpeech: string | null = null;

/** いま吹き出しに出している話を覚える（次にホームへ戻ったとき、同じ話を避ける） */
function RememberSpeech({ text }: { text: string }) {
  useEffect(() => {
    lastHomeSpeech = text;
  }, [text]);
  return null;
}

export default function Home() {
  const { userData, user, loading, isLoggedIn } = useAuth();
  const router = useRouter();
  const [spirits, setSpirits] = useState<Spirit[]>([]);
  /** 持っている精霊の数（手持ちに入れていない分も。レベルのカードの裏に出す）。読み込むまでは null */
  const [ownedSpiritCount, setOwnedSpiritCount] = useState<number | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [histories, setHistories] = useState<ReadHistoryEntry[]>([]);
  const [token, setToken] = useState<string | null>(null);
  /** 背景に重ねた黒い膜の濃さ。司書の明るさをこれに合わせる。背景を読み終えるまでは null */
  const [bgOverlay, setBgOverlay] = useState<number | null>(null);
  const [librarian, setLibrarian] = useState<HomeLibrarian | null>(null);
  /** スマホの幅の部品の配置（管理UIで公開したもの）。未公開なら null で今の並びのまま */
  const [layout, setLayout] = useState<HomeLayoutPieces | null>(null);
  /** 管理UIで隠した部品（スマホの幅でも広い画面でも出さない） */
  const [hiddenPieces, setHiddenPieces] = useState<HomePieceId[]>([]);
  /** 管理UIで変えた部品の文字・アイコン・行き先（空の欄は今のまま） */
  const [custom, setCustom] = useState<HomePieceCustoms>({});
  /** 今日の読み方（デイリーミッション）。右の列の欄と、司書の声かけに使う */
  const [daily, setDaily] = useState<DailyState | null>(null);
  /** プレゼントボックスの受け取っていない数（上から降りてくる知らせが5秒ごとに数え直す。届くまではログインのときの数） */
  const [presentCount, setPresentCount] = useState<number | null>(null);
  useEffect(() => {
    const onCount = (e: Event) => setPresentCount((e as CustomEvent<number>).detail);
    window.addEventListener(PRESENT_COUNT_EVENT, onCount);
    return () => window.removeEventListener(PRESENT_COUNT_EVENT, onCount);
  }, []);
  /** 司書のふだんの一言（管理UIの「司書の一言」で公開したもの）。開くたびに並びを混ぜる */
  const [librarianLines, setLibrarianLines] = useState<LibrarianLine[]>([]);
  /** 決まった知らせ（読み方の選び忘れ・未読の本）を「司書の一言」の一覧へ移したか。移す前は決まった知らせを先に出す */
  const [noticesInList, setNoticesInList] = useState(false);
  /** 吹き出しをタップした回数ぶん、次の一言へ進む */
  const [noteIndex, setNoteIndex] = useState(0);
  /** ほかの画面から戻ってきたか（初めて開いたときは知らせを先に出す。戻ってきたときは知らせも含めて抽選） */
  const [returning] = useState(() => homeOpenedBefore);
  /** 戻ってきたときの抽選の目（話の数が読み込みで変わっても同じ目で選ぶ） */
  const [draw] = useState(() => Math.random());
  /** 前に出ていた話（このホームを開いたときの値） */
  const [previousSpeech] = useState(() => lastHomeSpeech);
  useEffect(() => {
    homeOpenedBefore = true;
  }, []);
  // 画面の文（管理UIの「文言」で書き換えられる。src/lib/ui-texts.ts）
  const { t } = useUiTexts();

  useEffect(() => {
    if (!loading && !isLoggedIn) {
      router.replace("/login");
    }
  }, [loading, isLoggedIn, router]);

  useEffect(() => {
    fetch("/api/home-librarian")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: HomeLibrarian | null) => setLibrarian(data))
      .catch(() => setLibrarian(null));
    fetch("/api/home-layout")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { pieces: HomeLayoutPieces | null; hidden?: HomePieceId[]; custom?: HomePieceCustoms } | null) => {
        setLayout(data?.pieces ?? null);
        setHiddenPieces(data?.hidden ?? []);
        setCustom(data?.custom ?? {});
      })
      .catch(() => setLayout(null));
    fetch("/api/librarian-lines")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { lines?: LibrarianLine[]; noticesInList?: boolean } | null) => {
        // 「先頭に出す」一言は一覧の順のまま前に置き、ほかは混ぜる（混ぜた並びを一巡するので、全部出し切るまで同じ一言は出ない）
        const all = data?.lines ?? [];
        const rest = all.filter((line) => !line.first);
        for (let i = rest.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [rest[i], rest[j]] = [rest[j], rest[i]];
        }
        setLibrarianLines([...all.filter((line) => line.first), ...rest]);
        setNoticesInList(!!data?.noticesInList);
      })
      .catch(() => setLibrarianLines([]));
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const token = await user.getIdToken();
      setToken(token);
      const [allSpirits, histories, daily] = await Promise.all([
        apiFetch<Spirit[]>("/api/spirits", token).catch(() => []),
        apiFetch<ReadHistoryEntry[]>("/api/read-history", token).catch(() => []),
        apiFetch<DailyState>("/api/daily", token).catch(() => null),
      ]);
      setDaily(daily);
      const activeIds = userData?.activeSpirits ?? [];
      setSpirits((allSpirits || []).filter((s) => activeIds.includes(s.id)));
      setOwnedSpiritCount((allSpirits || []).length);
      setHistories(histories || []);
      setUnreadCount((histories || []).filter((h) => h.readCount === 0).length);
    })();
  }, [user, userData?.activeSpirits]);

  if (loading || !isLoggedIn || !userData) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-[var(--foreground)] opacity-60">{t("common.loading")}</p>
      </div>
    );
  }

  const totalPoints = userData.magicPoints.free + userData.magicPoints.paid;
  // 司書の声かけ。読み方を選び忘れたまま、ミッションで数える行動をしていたら、未読の知らせより先に知らせる
  const forgotDaily = !!daily && !daily.mode && daily.activityBeforeChoosing;
  // 吹き出しの話の並び: 知らせ（あれば）→ ふだんの一言（管理UIの「司書の一言」）。タップで次へ進み、最後まで行けば最初に戻る
  const supply = userData.magicSupply;
  const lineValues: LibrarianLineValues = {
    魔力: totalPoints,
    // 本は今日引くほど高くなるので、今日の残りの値段を順に払って数える
    引ける冊数: supply ? affordableDraws(supply, supply.drawsToday, totalPoints) : Math.floor(totalPoints / FALLBACK_GACHA_COST),
    "1冊の魔力": supply?.nextDrawPrice ?? FALLBACK_GACHA_COST,
    未読の冊数: unreadCount,
    ...(supply && {
      上限: supply.cap,
      上限まで: Math.max(0, supply.cap - userData.magicPoints.free),
      回復の量: supply.dailyAmount,
      回復の時刻: supply.dayStartHour,
      レベル: supply.level,
      読み終えた冊数: supply.uniqueReads,
      ...(supply.nextReads !== null && { 次のレベルまで: Math.max(1, supply.nextReads - supply.uniqueReads) }),
    }),
  };
  // 今日の読み方の状態（読み込むまでは分からないので入れない＝その条件の一言は出さない）
  const lineFacts: LibrarianLineFacts = daily ? { 読み方を選んだ: !!daily.mode, 読み方を選び忘れて行動した: forgotDaily } : {};
  // 決まった知らせ。「司書の一言」の一覧へ移した後は出さない（一覧の「先頭に出す」一言が代わりに出る）
  const notice = noticesInList
    ? null
    : forgotDaily
      ? { text: t("home.note.forgotDaily"), target: "/quests", link: t("home.note.chooseDaily") }
      : unreadCount > 0
        ? { text: t("home.note.unread", { 冊数: unreadCount }), target: "/shelf", link: t("home.note.openShelf") }
        : null;
  const usualSpeeches = librarianLines
    .map((line) => resolveLibrarianSpeech(line, lineValues, lineFacts))
    .filter((s): s is NonNullable<typeof s> => !!s)
    .map((s) => ({ text: s.text, target: s.link ? LIBRARIAN_LINE_LINKS[s.link.to].href : null, link: s.link?.label ?? null }));
  const speeches = [
    ...(notice ? [notice] : []),
    ...(usualSpeeches.length > 0 ? usualSpeeches : [{ text: t("home.note.default"), target: null, link: null }]),
  ];
  // 最初の話: 初めて開いたときは先頭（決まった知らせか「先頭に出す」一言、無ければ混ぜた一言の1つ目）。戻ってきたときは抽選し、前に出ていた話は避ける
  let firstSpeech = 0;
  if (returning && speeches.length > 1) {
    firstSpeech = Math.floor(draw * speeches.length);
    if (speeches[firstSpeech].text === previousSpeech) firstSpeech = (firstSpeech + 1) % speeches.length;
  }
  const speech = speeches[(firstSpeech + noteIndex) % speeches.length];
  const nextSpeech = () => setNoteIndex((i) => i + 1);
  // 今日の読み方のしおりは今日の読み方（/quests）、解放目標のしおりは解放目標・ログインボーナス（/goals）へ（10/12 に分けた）
  // 司書の明るさ。背景の黒い膜と同じだけ暗くし、管理UIで決めた分だけ明るく残す（管理UIの見本の librarianFilter と同じ式）。
  // 背景を読み終える（膜の濃さが決まる）までは司書を出さない
  const librarianFilter = librarian && bgOverlay !== null
    ? `brightness(${Math.min(1, Math.max(0, 1 - bgOverlay + librarian.brightnessLift))}) sepia(0.15) drop-shadow(0 0 14px rgba(255, 196, 120, 0.35))`
    : null;
  // 部品の置き場所を CSS に渡す（globals.css の .library-home[data-layout] .library-piece がスマホの幅でだけ使う）。
  // 下から測る部品は、ナビと同じく iPhone の下の余白（safe-area）の分だけ上げる
  const pieceStyle = (id: HomePieceId): CSSProperties | undefined => {
    const p = layout?.[id];
    if (!p) return undefined;
    const vars: Record<string, string | number> = { "--piece-x": p.x, "--piece-w": `${p.width ?? HOME_PIECE_WIDTH[id]}px` };
    if (p.height) vars["--piece-h"] = `${p.height}px`;
    if (p.anchor === "top") vars["--piece-top"] = `${p.offset}px`;
    else vars["--piece-bottom"] = `calc(${p.offset}px + env(safe-area-inset-bottom))`;
    return vars as CSSProperties;
  };
  const shown = (id: HomePieceId) => !hiddenPieces.includes(id);
  // スマホの幅の吹き出しのしっぽは、司書のいる側へ出す（どちらも中身の幅に対する横の割合。吹き出しの位置は未公開なら最初の並びの 0.308）
  const noteTail = (librarian?.positionX ?? 0.75) >= (layout?.note.x ?? 0.308) ? "right" : "left";
  /** 部品を押したときの行き先（管理UIで選んでいれば、その画面） */
  const go = (id: HomePieceId, fallback: string) => {
    const c = custom[id];
    router.push(c?.destination ? destinationHref(c.destination, c.view) : fallback);
  };
  // 書庫の部品の未読の数は、行き先が書庫のときだけ出す
  /** 部品のアイコン（管理UIで「アイコンなし」なら出さない） */
  const pieceIcon = (id: HomePieceId, fallback: LibraryIconName, props?: { width?: string; height?: string }) => {
    const name = custom[id]?.icon ?? fallback;
    return name === "none" ? null : <LibraryIcon name={name} {...props} />;
  };
  // ログインボーナスの画面で受け取れる品の数（ログインボーナスのしおりの朱の丸）
  const loginBonusCount = userData.loginBonusCount ?? 0;
  const shelfGoesToShelf = !custom.shelf?.destination || custom.shelf.destination === "shelf";

  return (
    <div className="library-home" data-layout={layout ? "" : undefined}>
      <PageBackground page="home" overrideId={userData.selectedBackgrounds?.home} onOverlayChange={setBgOverlay} />
      <a className="library-skip" href="#library-content">{t("home.skip")}</a>
      {/* その日はじめて開いたときの、貸出カードの窓（今日のログインボーナスを受け取っていなければ出る。document.body へ出す）。
          「あとで」にしても、ログインボーナスの画面の届くものを押して受け取れ、受け取れる数は下のしおりに出る（10/23） */}
      <LoginBonusWindow />
      <header className="library-header">
        <Link href="/" className="library-brand" aria-label={t("home.brandAria")}>
          <span className="library-monogram" aria-hidden="true">∞</span>
        </Link>
        <div className="library-header-actions">
          {/* 魔力は利用者カードのボタンの右に取り込んだ（10/12）。レベルの部品を隠しているときだけ、ここに出す */}
          {!(userData.magicSupply && shown("level")) && (
            <div className="library-magic"><span>{t("home.magic")}</span><strong>{totalPoints.toLocaleString("ja-JP")}</strong>{t("home.magicUnit") && <span>{t("home.magicUnit")}</span>}
              {userData.magicPoints.paid > 0 && <span className="sr-only">{t("home.magicBreakdown", { 無償: userData.magicPoints.free, 有償: userData.magicPoints.paid })}</span>}
            </div>
          )}
          {/* 受け取っていない贈り物の数を右上に小さく（10/12 本人の指示。報酬はすべてプレゼントボックスに届く） */}
          {(() => {
            const unclaimedPresents = presentCount ?? userData.presentCount ?? 0;
            return (
              <button type="button" className="library-icon-button library-present-button" data-unclaimed={unclaimedPresents > 0 || undefined} onClick={() => router.push("/presents")} aria-label={unclaimedPresents ? t("home.presentsCount", { 件数: unclaimedPresents }) : t("home.presents")} title={t("home.presents")}>
                <LibraryIcon name="gift" />
                {unclaimedPresents > 0 && <span className="library-present-count" aria-hidden="true">{unclaimedPresents > 99 ? "99+" : unclaimedPresents}</span>}
              </button>
            );
          })()}
        </div>
      </header>

      <main id="library-content" className="library-home-grid">
        <section className="library-sanctuary" aria-labelledby="library-welcome">
          <div className="library-arch" aria-hidden="true"><span>✦</span></div>
          <div className="library-welcome">
            <p className="library-eyebrow">{t("home.welcomeEyebrow")}</p>
            <h1 id="library-welcome"><TextLines text={t("home.welcomeTitle")} /></h1>
            <p className="library-intro"><TextLines text={t("home.welcomeIntro")} /></p>
          </div>
          {/* 広い画面の司書（アーチの中）。大きさと明るさは管理UIの値。スマホの幅では隠し、下の library-librarian-stage が立つ */}
          {librarian?.imageUrl && librarianFilter && (
            <div className="library-librarian" style={{ width: LIBRARIAN_BOX.width * librarian.scale, height: LIBRARIAN_BOX.height * librarian.scale }}>
              <Image src={librarian.imageUrl} alt={t("home.librarianAlt")} fill priority sizes="(max-width: 600px) 220px, 400px" className="object-contain object-bottom"
                style={{ filter: librarianFilter }} />
            </div>
          )}
          <div className="library-location">
            {token && shown("plaque") && <div className="library-piece" style={pieceStyle("plaque")}><CurrentLibraryPlaque token={token} onClick={() => go("plaque", "/backgrounds")} /></div>}
            {userData.magicSupply && shown("level") && <div className="library-piece" style={pieceStyle("level")}><UserLevelBadge supply={userData.magicSupply} freePoints={userData.magicPoints.free} userName={userData.displayName} magic={userData.magicPoints}
              records={{ shelfBooks: histories.length, ownedSpirits: ownedSpiritCount, loginDays: userData.loginDays ?? null, createdAt: userData.createdAt }} /></div>}
          </div>
          {shown("note") && <div className="library-piece" style={pieceStyle("note")}><div className="library-librarian-note" data-tail={noteTail}>
            <RememberSpeech text={speech.text} />
            {/* 見出しは既定で出さない（管理UIで文を入れたときだけ） */}
            {t("home.note.eyebrow") && <span className="library-eyebrow">{t("home.note.eyebrow")}</span>}
            {/* 文節の切れ目でだけ折る（スマホの幅では欄が狭く、ふつうに折ると「出 / 会える」のように語の途中で切れる） */}
            {/* タップで次の一言へ（話が一つだけなら切り替えない） */}
            {speeches.length > 1 ? (
              <p role="button" tabIndex={0} onClick={nextSpeech} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nextSpeech(); } }} className="cursor-pointer select-none">
                <PhraseText text={speech.text} />
              </p>
            ) : (
              <p><PhraseText text={speech.text} /></p>
            )}
            {speech.target && <button type="button" onClick={() => speech.target && router.push(speech.target)} className="library-text-link">{speech.link}<LibraryIcon name="arrow" width="17" height="17" /></button>}
          </div></div>}
        </section>

        <section className="library-desk" aria-labelledby="library-desk-title">
          <div className="library-section-heading"><span className="library-eyebrow">{t("home.desk.eyebrow")}</span><h2 id="library-desk-title">{t("home.desk.title")}</h2><span className="library-heading-rule" /></div>
          {shown("continue") && <div className="library-piece" style={pieceStyle("continue")}><ContinueReadingCard histories={histories} onOpen={(id) => router.push(`/read/${id}`)} /></div>}
          {shown("gacha") && <div className="library-piece" style={pieceStyle("gacha")}><button type="button" onClick={() => go("gacha", GACHA_ENTRY_PATH)} className="library-discover library-bookmark" data-mark="shippo">
            {custom.gacha?.icon !== "none" && <span className="library-discover-icon">{pieceIcon("gacha", "book", { width: "32", height: "32" })}</span>}
            <span><span className="library-eyebrow">{t("home.gacha.eyebrow")}</span><strong>{t("home.gacha.title")}</strong><span className="library-discover-caption">{t("home.gacha.caption")}</span></span>
            <LibraryIcon name="arrow" className="library-discover-arrow" />
          </button></div>}
          {(shown("shelf") || shown("map")) && <div className="library-shortcuts">
            {shown("shelf") && <div className="library-piece" style={pieceStyle("shelf")}><button type="button" onClick={() => go("shelf", "/shelf")} className="library-bookmark" data-mark="hishi">{pieceIcon("shelf", "shelf")}<span>{t("home.shelf.title")}<small>{shelfGoesToShelf && unreadCount > 0 ? t("home.shelf.unread", { 冊数: unreadCount }) : t("home.shelf.caption")}</small></span><span aria-hidden="true">›</span></button></div>}
            {shown("map") && <div className="library-piece" style={pieceStyle("map")}><button type="button" onClick={() => go("map", "/map")} className="library-bookmark" data-mark="uroko">{pieceIcon("map", "map")}<span>{t("home.map.title")}<small>{t("home.map.caption")}</small></span><span aria-hidden="true">›</span></button></div>}
          </div>}
          {/* 広い画面の今日の読み方の欄は「今日の読み方・解放目標」の部品と同じ中身。隠しても消さずに見えなくする（解放目標の受け取れる数を上の帯の点に渡しているため） */}
          {shown("quests") && <div className="library-section-heading library-section-heading-small"><h2>{t("home.daily.heading")}</h2><span className="library-heading-rule" /></div>}
          <div className="library-daily" style={shown("quests") ? undefined : { display: "none" }}>
            <DailyMissionsCompact state={daily} onOpen={() => go("quests", "/quests")} />
            {token && <SkillTreeGoals token={token} line onOpenAll={() => router.push("/goals")} />}
          </div>
          {/* 今日の読み方を選んだ後は、選んだ付箋・確かめる栞と同じ色と七宝にし、名前を選んだ読み方にする（10/12 本人の指示。globals.css の .library-bookmark[data-mode]） */}
          {shown("quests") && <div className="library-piece" style={pieceStyle("quests")}><button type="button" className="library-mobile-quests library-bookmark" data-mark="ichimatsu" data-mode={daily?.mode ?? undefined} onClick={() => go("quests", "/quests")}>
            {pieceIcon("quests", "quest", { width: "20", height: "20" })}
            {daily?.mode && daily.modes[daily.mode]?.label
              ? <span className="library-quest-name"><small>{t("home.quests.title")}</small><span>{daily.modes[daily.mode].label}</span></span>
              : <span>{t("home.quests.title")}</span>}
            <span className="library-mobile-quest-status">{daily && !daily.mode ? t("home.quests.choose") : t("home.quests.check")} ›</span>
          </button></div>}
          {/* ミッション（解放目標・ログインボーナス。10/12 に今日の読み方から分けた）。受け取れるログインボーナスがあれば › の前に品の数を朱の丸で出し、押すとログインボーナスのタブを開く（10/23） */}
          {shown("goals") && <div className="library-piece" style={pieceStyle("goals")}><button type="button" className="library-mobile-quests library-goals library-bookmark" data-mark="kikko" onClick={() => go("goals", loginBonusCount > 0 ? "/goals?tab=login" : "/goals")}>
            {pieceIcon("goals", "target", { width: "20", height: "20" })}
            {/* 1行目に名前と受け取れる件数、2行目に小さな説明（文言で入れたときだけ。幅 208px のしおりでも折れないように） */}
            <span className="library-goals-text">
              <span className="library-goals-row"><span><TextLines text={t("home.goals.title")} /></span><span className="library-mobile-quest-status">
                {loginBonusCount > 0 && <><span className="library-goals-count" aria-hidden="true">{loginBonusCount > 99 ? "99+" : loginBonusCount}</span><span className="sr-only">{t("home.goals.loginBonusCount", { 件数: loginBonusCount })}</span></>}
                ›
              </span></span>
              {t("home.goals.caption") && <small>{t("home.goals.caption")}</small>}
            </span>
          </button></div>}
          {shown("companions") && <div className="library-piece" style={pieceStyle("companions")}><div className="library-companions">
            <span className="library-eyebrow">{t("home.companions.eyebrow")}</span>
            {spirits.length > 0 ? <SpiritStripBookmark spirits={spirits} handSlotCount={userData.handSlotCount ?? 1} onClick={() => go("companions", "/party")} /> : (
              <button type="button" onClick={() => go("companions", "/spirit")} className="library-companion-empty"><LibraryIcon name="spirit" /><span>{t("home.companions.empty")}<small>{t("home.companions.emptyNote")}</small></span><span aria-hidden="true">›</span></button>
            )}
          </div></div>}
          <p className="library-desk-footer"><span aria-hidden="true">✧</span> {t("home.footer")}</p>
        </section>
      </main>

      {/* スマホの幅の司書。位置・大きさ・明るさは管理UIの「ホームの司書」の見本（幅 390px）と同じ決め方：
          中身の幅（最大 448px）に対する横の割合、画面の下端から絵の下端までの px。欄（library-home-grid）の後ろ・背景の手前に立つ */}
      {librarian?.imageUrl && librarianFilter && (
        <div className="library-librarian-stage">
          <div
            className="absolute -translate-x-1/2"
            style={{
              left: `${librarian.positionX * 100}%`,
              bottom: librarian.positionBottom,
              width: LIBRARIAN_BOX.width * librarian.scale,
              height: LIBRARIAN_BOX.height * librarian.scale,
            }}
          >
            <Image src={librarian.imageUrl} alt={t("home.librarianAlt")} fill priority sizes={`${Math.round(LIBRARIAN_BOX.width * librarian.scale)}px`} className="object-contain object-bottom"
              style={{ filter: librarianFilter }} />
          </div>
        </div>
      )}
    </div>
  );
}
